import { Box } from "@mui/material";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import Flex from "@/components/Flex";
import LoadingOverlay from "@/components/LoadingOverlay";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { useQueryParams } from "@/hooks/use-query-param";
import { useRegisterSectionSave } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import AllScreeningAssessment from "./AllScreeningAssessment";
import FreeTextScreeningForm from "./FreeTextScreeningForm";
import {
  SCREENING_FORM_MAX_HEIGHT,
  SCREENING_NO_QUESTIONS_MESSAGE,
  SCREENING_SAVE_LABEL,
  TitleMapper,
} from "./assessment-constant";
import { useScreeningAssessment } from "./hooks/use-screening-assessment";

export interface ScreeningAssessmentProps {
  type: string;
  /** Queries run only while enabled, so a collapsed note section does not fetch. */
  isEnabled?: boolean;
  disabled?: boolean;
  onSaved?: () => void;
}

/** A screener filled in inline: its questions, the appointment's saved answers and a Save button. */
const ScreeningAssessment = ({
  type,
  isEnabled = true,
  disabled,
  onSaved,
}: ScreeningAssessmentProps) => {
  const appointmentId = useQueryParams("appointmentid");
  const { patientId } = useParams<{ patientId: string }>();
  const { questions, tableData, freeText, isLoading, setRadio, isDirty, save } =
    useScreeningAssessment({
      type,
      appointmentId,
      patientId,
      enabled: isEnabled,
    });
  // Inside a visit note the screener is flushed with Save & Close / Save & Sign; elsewhere the registry is a no-op.
  const flush = useCallback(() => save(onSaved), [save, onSaved]);
  useRegisterSectionSave(type, !disabled && isDirty, flush);

  return (
    <Box position="relative" display="flex" flexDirection="column" gap={1.5}>
      <LoadingOverlay isLoading={isLoading} />
      <Box
        sx={{
          maxHeight: SCREENING_FORM_MAX_HEIGHT,
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        {freeText.def ? (
          <FreeTextScreeningForm
            def={freeText.def}
            questions={questions}
            values={freeText.values}
            onChange={freeText.setValue}
            titleDetail={TitleMapper[type]}
            disabled={disabled}
          />
        ) : questions.length ? (
          <AllScreeningAssessment
            tableData={tableData as never}
            disabled={disabled}
            handleRadioChange={setRadio}
            titleDetail={TitleMapper[type]}
          />
        ) : (
          !isLoading && (
            <Text size={13} c="text.secondary">
              {SCREENING_NO_QUESTIONS_MESSAGE}
            </Text>
          )
        )}
      </Box>
      {!disabled && !!questions.length && (
        <Flex justifyContent="flex-end">
          <StyledButtonV2
            label={SCREENING_SAVE_LABEL}
            variant="contained"
            onClick={() => save(onSaved)}
          />
        </Flex>
      )}
    </Box>
  );
};

export default ScreeningAssessment;
